import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Trophy, Flame, Target, CheckCircle2, Circle, AlertTriangle, Loader2,
  Sparkles, Mail, Repeat, FastForward, X, Calendar, Rocket, BarChart3,
  Lock, MessageSquare, FileText, Phone, PenTool, Wrench, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const CHALLENGE_DURATION_DAYS = 90;

const PRICING_OPTIONS = [
  { v: '$ — Under $500', label: '$ — Under $500 (volume play)' },
  { v: '$$ — $500–$2,500', label: '$$ — $500–$2,500 (mid-ticket)' },
  { v: '$$$ — $2,500–$10,000', label: '$$$ — $2,500–$10,000 (high-ticket)' },
  { v: '$$$$ — $10,000+', label: '$$$$ — $10,000+ (enterprise)' },
  { v: 'Recurring — Under $200/mo', label: 'Recurring — Under $200/mo' },
  { v: 'Recurring — $200–$2,000/mo', label: 'Recurring — $200–$2,000/mo' },
  { v: 'Recurring — $2,000+/mo', label: 'Recurring — $2,000+/mo' },
];

const CATEGORY_META = {
  outreach: { label: 'Outreach', icon: MessageSquare, color: 'bg-orange-100 text-orange-700 border-orange-200' },
  follow_up: { label: 'Follow-up', icon: Repeat, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  content: { label: 'Content', icon: PenTool, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  setup: { label: 'Setup', icon: Wrench, color: 'bg-slate-100 text-slate-700 border-slate-200' },
  sales: { label: 'Sales', icon: Phone, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  delivery: { label: 'Delivery', icon: FileText, color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
};

const Challenge = () => {
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [adapting, setAdapting] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  // Setup form
  const [setupForm, setSetupForm] = useState({
    service: '',
    pricing: '',
    target_audience: '',
    hours_per_week: 10,
  });
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    fetchChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchChallenge = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/challenge/active`);
      setChallenge(data.challenge);
      if (data.challenge?.progress) {
        setSelectedDay(data.challenge.progress.current_day);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const startChallenge = async () => {
    if (!setupForm.service.trim() || !setupForm.pricing) {
      toast.error('Tell us your service and pricing');
      return;
    }
    setStarting(true);
    try {
      const { data } = await axios.post(`${API_URL}/challenge/start`, setupForm, { timeout: 180000 });
      setChallenge(data.challenge);
      setSelectedDay(data.challenge?.progress?.current_day || 1);
      toast.success('Your 90-day challenge is live. Day 1 begins now.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start challenge');
    } finally {
      setStarting(false);
    }
  };

  const toggleTask = async (day, taskId, completed) => {
    // optimistic update
    setChallenge(prev => {
      if (!prev) return prev;
      const next = { ...prev, days: prev.days.map(d => ({ ...d, tasks: d.tasks.map(t => ({ ...t })) })) };
      const dObj = next.days.find(d => d.day === day);
      const tObj = dObj?.tasks.find(t => t.id === taskId);
      if (tObj) tObj.completed = completed;
      return next;
    });
    try {
      const { data } = await axios.post(`${API_URL}/challenge/task/toggle`, { day, task_id: taskId, completed });
      setChallenge(data.challenge);
    } catch (e) {
      toast.error('Failed to save. Reverting.');
      fetchChallenge();
    }
  };

  const refreshInsights = async () => {
    setInsightsLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/challenge/insights`);
      setInsights(data.insights);
      toast.success('Coach update ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate insights');
    } finally {
      setInsightsLoading(false);
    }
  };

  const adapt = async (mode) => {
    setAdapting(true);
    try {
      const { data } = await axios.post(`${API_URL}/challenge/adapt`, { mode }, { timeout: 180000 });
      setChallenge(data.challenge);
      toast.success(mode === 'shift' ? 'Overdue tasks shifted forward' : 'Remaining days regenerated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Adaptation failed');
    } finally {
      setAdapting(false);
    }
  };

  const sendReminder = async () => {
    setEmailSending(true);
    try {
      await axios.post(`${API_URL}/challenge/send-reminder`);
      toast.success("Today's reminder emailed");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send reminder');
    } finally {
      setEmailSending(false);
    }
  };

  const endChallenge = async (outcome) => {
    if (!window.confirm(outcome === 'completed'
      ? 'Mark this challenge as completed and locked in your first paying client?'
      : 'Quit this challenge? Past progress will be saved to history.')) return;
    try {
      await axios.post(`${API_URL}/challenge/end`, {
        outcome,
        client_landed: outcome === 'completed',
      });
      toast.success(outcome === 'completed' ? '🎉 Challenge marked complete' : 'Challenge ended');
      setChallenge(null);
      setInsights(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update challenge');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!challenge) {
    return <SetupView form={setupForm} setForm={setSetupForm} onStart={startChallenge} starting={starting} />;
  }

  return (
    <ActiveView
      challenge={challenge}
      insights={insights}
      insightsLoading={insightsLoading}
      adapting={adapting}
      emailSending={emailSending}
      selectedDay={selectedDay}
      setSelectedDay={setSelectedDay}
      toggleTask={toggleTask}
      refreshInsights={refreshInsights}
      adapt={adapt}
      sendReminder={sendReminder}
      endChallenge={endChallenge}
    />
  );
};

// ==================== SETUP VIEW ====================

const SetupView = ({ form, setForm, onStart, starting }) => {
  return (
    <DashboardLayout>
      <Header
        title="90-Day Client Acquisition Challenge"
        subtitle="A personalized, AI-built daily playbook to land your first paying client in 90 days."
      />
      <div className="p-6 max-w-5xl mx-auto">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 via-rose-500 to-pink-600 p-8 text-white mb-8">
          <div className="absolute -right-12 -top-12 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute right-20 bottom-0 w-40 h-40 bg-yellow-300/20 rounded-full blur-2xl" />
          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold mb-4">
              <Flame className="w-3.5 h-3.5" /> 90-DAY ACCOUNTABILITY PROGRAM
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">First paying client. 90 days. No fluff.</h2>
            <p className="text-white/90 text-base">
              Tell us what you sell. We build a day-by-day execution plan with measurable outreach
              quotas, follow-ups, content, and sales actions. You ship daily; we hold you accountable.
            </p>
            <div className="flex flex-wrap gap-2 mt-4 text-xs">
              {['Daily checklist', 'AI accountability', 'Adaptive replanning', 'Email reminders'].map(b => (
                <span key={b} className="bg-white/15 px-2.5 py-1 rounded-full">{b}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { i: Target, t: 'Daily outreach quota', s: 'Personalized to your weekly hours.' },
            { i: BarChart3, t: 'Track every task', s: 'Streaks, completion %, critical-task hit rate.' },
            { i: Rocket, t: 'Adaptive coaching', s: 'AI shifts overdue work + regenerates if you fall behind.' },
          ].map(({ i: Icon, t, s }) => (
            <div key={t} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-orange-600" />
              </div>
              <p className="font-semibold text-slate-900">{t}</p>
              <p className="text-sm text-slate-500 mt-1">{s}</p>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Build my plan</h3>
            <p className="text-sm text-slate-500">Two minutes. Then your daily roadmap is live.</p>
          </div>

          <div>
            <Label htmlFor="service" className="mb-1.5 block">What service do you offer?</Label>
            <Textarea
              id="service"
              data-testid="challenge-service-input"
              value={form.service}
              onChange={(e) => setForm(f => ({ ...f, service: e.target.value }))}
              placeholder="e.g. 1-on-1 strength coaching for busy dads. Or: Done-for-you LinkedIn ghostwriting for B2B founders."
              rows={3}
              className="resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1.5 block">Pricing</Label>
              <Select value={form.pricing} onValueChange={(v) => setForm(f => ({ ...f, pricing: v }))}>
                <SelectTrigger data-testid="challenge-pricing-select"><SelectValue placeholder="Choose price tier" /></SelectTrigger>
                <SelectContent>
                  {PRICING_OPTIONS.map(o => (<SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="hpw" className="mb-1.5 block">Hours per week (your time budget)</Label>
              <Input
                id="hpw"
                type="number"
                min={2}
                max={60}
                value={form.hours_per_week}
                onChange={(e) => setForm(f => ({ ...f, hours_per_week: e.target.value }))}
                data-testid="challenge-hours-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="audience" className="mb-1.5 block">Who is the ideal customer? <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea
              id="audience"
              data-testid="challenge-audience-input"
              value={form.target_audience}
              onChange={(e) => setForm(f => ({ ...f, target_audience: e.target.value }))}
              placeholder="e.g. Solo founders running SaaS at $5–50k MRR who don't have time to write."
              rows={2}
              className="resize-none"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-slate-500">
              <Sparkles className="inline w-3.5 h-3.5 -mt-0.5 mr-1" />
              AI will tailor 90 days of action — outreach, follow-ups, content, sales.
            </p>
            <Button
              onClick={onStart}
              disabled={starting}
              data-testid="challenge-start-btn"
              className="bg-gradient-to-r from-orange-500 to-rose-600 hover:from-orange-600 hover:to-rose-700 text-white px-6"
            >
              {starting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating plan…</>) : (<><Rocket className="w-4 h-4 mr-2" />Start my 90 days</>)}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// ==================== ACTIVE VIEW ====================

const ActiveView = ({
  challenge, insights, insightsLoading, adapting, emailSending,
  selectedDay, setSelectedDay,
  toggleTask, refreshInsights, adapt, sendReminder, endChallenge,
}) => {
  const progress = challenge.progress || {};
  const todayObj = useMemo(
    () => challenge.days.find(d => d.day === progress.current_day),
    [challenge.days, progress.current_day]
  );
  const selectedDayObj = useMemo(
    () => challenge.days.find(d => d.day === selectedDay) || todayObj,
    [challenge.days, selectedDay, todayObj]
  );

  return (
    <DashboardLayout>
      <Header
        title="90-Day Challenge"
        subtitle={`${challenge.service} · ${challenge.pricing}`}
      />
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Stats Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="challenge-stats">
          <StatCard icon={Calendar} label="Day" value={`${progress.current_day}/${progress.duration_days || CHALLENGE_DURATION_DAYS}`} accent="indigo" />
          <StatCard icon={Flame} label="Streak" value={`${progress.streak} 🔥`} accent="orange" />
          <StatCard icon={CheckCircle2} label="Done" value={`${progress.completion_pct}%`} accent="emerald" />
          <StatCard icon={Target} label="Outreach" value={`${progress.outreach_done_count}/${progress.outreach_due}`} accent="rose" sub={`${progress.outreach_pct}%`} />
          <StatCard icon={Trophy} label="Critical hits" value={`${progress.critical_done}/${progress.critical_due}`} accent="amber" />
        </div>

        {/* Progress bar */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-medium text-slate-700">Plan Progress</p>
            <p className="text-sm text-slate-500">{progress.completed_tasks} / {progress.total_tasks} tasks complete</p>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 via-rose-500 to-pink-600 transition-all"
              style={{ width: `${progress.completion_pct || 0}%` }}
            />
          </div>
          {challenge.summary && (
            <p className="text-xs text-slate-500 mt-3 italic">"{challenge.summary}"</p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT — Day picker + tasks */}
          <div className="lg:col-span-2 space-y-5">
            {/* Day picker */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-slate-700">Roadmap</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedDay(progress.current_day)}
                  data-testid="challenge-jump-today"
                >
                  Jump to today
                </Button>
              </div>
              <DayGrid
                days={challenge.days}
                currentDay={progress.current_day}
                selectedDay={selectedDay}
                onSelect={setSelectedDay}
              />
            </div>

            {/* Selected day tasks */}
            {selectedDayObj && (
              <div
                className={`border-2 rounded-xl p-5 ${
                  selectedDayObj.day === progress.current_day
                    ? 'bg-gradient-to-br from-orange-50 to-rose-50 border-orange-200'
                    : 'bg-white border-slate-200'
                }`}
                data-testid="challenge-day-card"
              >
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
                        Week {selectedDayObj.week} · Day {selectedDayObj.day}
                      </span>
                      {selectedDayObj.milestone && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                          <Trophy className="w-3 h-3" /> Milestone
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mt-2">{selectedDayObj.focus}</h3>
                    <p className="text-sm text-slate-600 mt-1">{selectedDayObj.goal}</p>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {selectedDayObj.tasks?.map((t) => (
                    <TaskRow
                      key={t.id}
                      task={t}
                      isPast={selectedDayObj.day < progress.current_day}
                      onToggle={(checked) => toggleTask(selectedDayObj.day, t.id, checked)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Adaptive controls */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <p className="text-sm font-semibold text-slate-700 mb-1">Adapt the plan</p>
              <p className="text-xs text-slate-500 mb-4">Falling behind or pivoting? Coach can recalibrate.</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => adapt('shift')}
                  disabled={adapting}
                  data-testid="challenge-shift-btn"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  {adapting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FastForward className="w-3.5 h-3.5 mr-1.5" />}
                  Shift overdue forward
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => adapt('regenerate')}
                  disabled={adapting}
                  data-testid="challenge-regenerate-btn"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                >
                  {adapting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Repeat className="w-3.5 h-3.5 mr-1.5" />}
                  Regenerate days {progress.current_day}–{progress.duration_days || CHALLENGE_DURATION_DAYS}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={sendReminder}
                  disabled={emailSending}
                  data-testid="challenge-email-btn"
                >
                  {emailSending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                  Email me today's plan
                </Button>
              </div>
              {challenge.last_adaptation_summary && (
                <p className="text-xs text-slate-500 mt-3 italic">{challenge.last_adaptation_summary}</p>
              )}
            </div>

            {/* End controls */}
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-600">Landed your first paying client? Mark it as won.</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => endChallenge('abandoned')} data-testid="challenge-quit-btn">
                  Quit
                </Button>
                <Button size="sm" onClick={() => endChallenge('completed')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="challenge-complete-btn">
                  <Trophy className="w-3.5 h-3.5 mr-1.5" /> Mark complete
                </Button>
              </div>
            </div>
          </div>

          {/* RIGHT — Coach insights */}
          <div className="space-y-5">
            <CoachPanel
              insights={insights}
              loading={insightsLoading}
              onRefresh={refreshInsights}
              progress={progress}
              overdueCount={progress.overdue_critical_tasks?.length || 0}
            />

            {/* Channel + audience */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Game plan</p>
              <div className="space-y-3">
                <InfoRow label="Primary channel" value={challenge.primary_channel || 'Multi-channel'} />
                <InfoRow label="Daily outreach target" value={`${challenge.daily_outreach_target}/day`} />
                {challenge.industry && <InfoRow label="Industry" value={challenge.industry} />}
                <InfoRow label="Time budget" value={`${challenge.hours_per_week}h/week`} />
              </div>
            </div>

            {/* Overdue list */}
            {(progress.overdue_critical_tasks?.length || 0) > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <p className="text-sm font-semibold text-red-900">Overdue critical tasks</p>
                </div>
                <ul className="space-y-2">
                  {progress.overdue_critical_tasks.slice(0, 5).map((t, i) => (
                    <li key={i} className="text-xs text-red-800 flex gap-2">
                      <span className="font-bold">D{t.day}</span>
                      <span>{t.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// ==================== Subcomponents ====================

const StatCard = ({ icon: Icon, label, value, sub, accent }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    rose: 'bg-rose-50 text-rose-700 border-rose-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${colors[accent]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 opacity-70" />
        <p className="text-[10px] uppercase font-bold tracking-wider opacity-80">{label}</p>
      </div>
      <p className="text-xl font-bold">{value}</p>
      {sub && <p className="text-xs opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};

const DayGrid = ({ days, currentDay, selectedDay, onSelect }) => {
  return (
    <div className="grid grid-cols-7 sm:grid-cols-10 gap-1.5">
      {days.map((d) => {
        const isPast = d.day < currentDay;
        const isToday = d.day === currentDay;
        const isFuture = d.day > currentDay;
        const isSelected = d.day === selectedDay;
        const allDone = d.tasks?.length > 0 && d.tasks.every(t => t.completed);
        const someDone = d.tasks?.some(t => t.completed);

        let cls = 'bg-white border-slate-200 text-slate-400 hover:border-slate-300';
        if (allDone) cls = 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600';
        else if (someDone && isPast) cls = 'bg-amber-100 border-amber-300 text-amber-800';
        else if (isPast) cls = 'bg-red-50 border-red-200 text-red-700';
        else if (isToday) cls = 'bg-orange-500 text-white border-orange-600 hover:bg-orange-600 ring-2 ring-orange-300';
        else if (isFuture) cls = 'bg-slate-50 border-slate-200 text-slate-500';

        const ringCls = isSelected ? 'ring-2 ring-indigo-500 ring-offset-1' : '';

        return (
          <button
            key={d.day}
            onClick={() => onSelect(d.day)}
            data-testid={`challenge-day-${d.day}`}
            className={`relative aspect-square rounded-lg border text-xs font-semibold transition-all ${cls} ${ringCls}`}
            title={`Day ${d.day} · ${d.focus}`}
          >
            {d.day}
            {d.milestone && (
              <Trophy className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 text-amber-500 bg-white rounded-full p-0.5 border border-amber-300" />
            )}
            {isFuture && !isToday && (
              <Lock className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 opacity-50" />
            )}
          </button>
        );
      })}
    </div>
  );
};

const TaskRow = ({ task, isPast, onToggle }) => {
  const meta = CATEGORY_META[task.category] || CATEGORY_META.outreach;
  const Icon = meta.icon;
  const overdueCritical = isPast && !task.completed && task.is_critical;

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
        task.completed ? 'bg-emerald-50 border-emerald-200' :
        overdueCritical ? 'bg-red-50 border-red-200' :
        'bg-white border-slate-200 hover:border-slate-300'
      }`}
      data-testid={`task-row-${task.id}`}
    >
      <Checkbox
        checked={!!task.completed}
        onCheckedChange={onToggle}
        data-testid={`task-check-${task.id}`}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${task.completed ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
          {task.label}
        </p>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${meta.color}`}>
            <Icon className="w-2.5 h-2.5" />
            {meta.label}
          </span>
          {task.target_count > 0 && (
            <span className="text-xs font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full">
              Target: {task.target_count}
            </span>
          )}
          {task.is_critical && (
            <span className="text-[10px] font-bold uppercase text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
              Critical
            </span>
          )}
          {overdueCritical && (
            <span className="text-[10px] font-bold uppercase text-amber-700 inline-flex items-center gap-0.5">
              <AlertTriangle className="w-3 h-3" /> Overdue
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const CoachPanel = ({ insights, loading, onRefresh, progress, overdueCount }) => {
  const grade = insights?.performance_grade;
  const gradeColors = {
    A: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    B: 'bg-blue-100 text-blue-700 border-blue-300',
    C: 'bg-amber-100 text-amber-700 border-amber-300',
    D: 'bg-orange-100 text-orange-700 border-orange-300',
    F: 'bg-red-100 text-red-700 border-red-300',
  };
  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-5 shadow-xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <p className="text-sm font-bold">AI Coach</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Accountability check-in</p>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRefresh}
          disabled={loading}
          data-testid="challenge-coach-refresh"
          className="text-slate-300 hover:text-white hover:bg-slate-700"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {!insights ? (
        <div className="text-center py-8">
          <p className="text-sm text-slate-400 mb-3">Tap below for today's accountability check-in.</p>
          <Button
            onClick={onRefresh}
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="challenge-coach-generate"
          >
            {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Analyzing…</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Get my check-in</>}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            {grade && (
              <div className={`flex-shrink-0 w-12 h-12 rounded-lg border-2 flex items-center justify-center font-bold text-lg ${gradeColors[grade] || 'bg-slate-700 text-white'}`}>
                {grade}
              </div>
            )}
            <p className="text-sm font-semibold leading-snug">{insights.headline}</p>
          </div>

          {insights.today_priority && (
            <div className="bg-orange-500/20 border border-orange-400/40 rounded-lg p-3">
              <p className="text-[10px] uppercase font-bold text-orange-300 tracking-wider mb-1">Today's #1 priority</p>
              <p className="text-sm">{insights.today_priority}</p>
            </div>
          )}

          {insights.wins?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase font-bold text-emerald-400 tracking-wider mb-1">Wins</p>
              <ul className="space-y-1">
                {insights.wins.map((w, i) => (
                  <li key={i} className="text-xs text-slate-200 flex gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insights.gaps?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase font-bold text-amber-400 tracking-wider mb-1">Gaps to close</p>
              <ul className="space-y-1">
                {insights.gaps.map((g, i) => (
                  <li key={i} className="text-xs text-slate-200 flex gap-1.5">
                    <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {insights.motivation && (
            <div className="border-t border-slate-700 pt-3">
              <p className="text-xs italic text-slate-300">"{insights.motivation}"</p>
            </div>
          )}

          {insights.next_step && (
            <div className="bg-slate-700/50 rounded-lg p-3">
              <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1">Next 60 minutes</p>
              <p className="text-xs text-slate-100">{insights.next_step}</p>
            </div>
          )}

          {overdueCount > 0 && (
            <p className="text-[11px] text-red-300 inline-flex items-center gap-1">
              <X className="w-3 h-3" /> {overdueCount} critical task(s) overdue
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">{label}</p>
    <p className="text-sm text-slate-900 font-medium mt-0.5">{value}</p>
  </div>
);

export default Challenge;
